import { useCallback, useState } from "react";
import { v4 } from "uuid";
import { api } from "../../../api";
import { WhatsappTemplateButtonTypeEnum } from "../../../enums/whatsapp-template-button-type.enum";
import { ensureWhatsappFlowLinked } from "../../../functions/ensureWhatsappFlowLinked";
import LoadingSpinner from "../../../externalComponents/LoadingSpinner";
import { Button } from "../../../externalComponents/tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../externalComponents/tailwind/dialog";
import SelectInput from "../../../externalComponents/tailwind/select-input";
import { TemplatePreview } from "../../../externalComponents/TemplatePreview";

export function ConversationSendTemplateDialog({
  customerId,
  allTemplatesOptions,
  selectedTemplate,
  setSelectedTemplate,
  template,
  allTemplatesData,
  phoneNumber,
  bsuid,
  modalRef,
  setModalRef,
}) {
  const closeModal = useCallback(
    () => setModalRef((_modalRef) => ({ ..._modalRef, sendTemplate: false })),
    [setModalRef],
  );

  const [isSendingLoading, setIsSendingLoading] = useState(false);

  const sendTemplate = async () => {
    setIsSendingLoading(true);
    try {
      // Create a whatsappTemplate object
      const selectedTemplateData = allTemplatesData.find(
        (t) => t.name === selectedTemplate,
      );
      const headerText =
        selectedTemplateData.components?.find(
          (c) => c.format === "TEXT" && c.type === "HEADER",
        )?.text ?? "";
      const bodyText =
        selectedTemplateData.components?.find((c) => c.type === "BODY")?.text ??
        "";
      const footerText =
        selectedTemplateData.components?.find((c) => c.type === "FOOTER")
          ?.text ?? "";
      const body = `${headerText ? headerText + "\n\n" : ""}${
        bodyText ? bodyText + "\n\n" : ""
      }${footerText}`;
      const replyButtons = selectedTemplateData.components
        ?.find((c) => c.type === "BUTTONS")
        ?.buttons?.filter((b) => b.type === "QUICK_REPLY")
        .map((b) => {
          return {
            create: {
              customer: { _link: customerId },
              whatsappId: v4(),
              title: b.text,
            },
          };
        });
      const urlButton = selectedTemplateData.components
        ?.find((c) => c.type === "BUTTONS")
        ?.buttons?.find((b) => b.type === "URL");

      const flowButtonData = selectedTemplateData.components
        ?.find((c) => c.type === "BUTTONS")
        ?.buttons?.find((b) => b.type === WhatsappTemplateButtonTypeEnum.FLOW);

      const whatsappFlowId = flowButtonData
        ? await ensureWhatsappFlowLinked({
            customerId,
            flowId: flowButtonData.flow_id,
          })
        : null;

      var officialWhatsappTemplate;
      if (urlButton) {
        officialWhatsappTemplate =
          await api.officialWhatsappTemplate.maybeFindFirst({
            filter: {
              name: { equals: selectedTemplate },
            },
            select: {
              id: true,
              buttonUrlLink: true,
              buttonUrlLink2: true,
            },
          });
      }

      const whatsappTemplate = await api.whatsappTemplate.create({
        customer: { _link: customerId },
        whatsappTemplateName: selectedTemplate,

        ...(whatsappFlowId && { whatsappFlow: { _link: whatsappFlowId } }),

        ...(officialWhatsappTemplate && {
          redirectionUrl: {
            create: {
              customer: { _link: customerId },
              url: officialWhatsappTemplate.buttonUrlLink,
              uuid: v4(),
            },
          },
        }),

        ...(officialWhatsappTemplate &&
          officialWhatsappTemplate?.buttonUrlLink2 && {
            redirectionUrl2: {
              create: {
                customer: { _link: customerId },
                url: officialWhatsappTemplate.buttonUrlLink2,
                uuid: v4(),
              },
            },
          }),
        messageSkeleton: {
          create: {
            customer: { _link: customerId },
            body: body,
            replyButtons: replyButtons,
            urlButtonText: urlButton?.text,
            urlButtonLink: urlButton?.url,
          },
        },
      });
      console.log(whatsappTemplate.id);
      await api.sendWhatsAppTemplate({
        phoneNumber,
        whatsappTemplateId: whatsappTemplate.id,
        bsuid,
        isForced: true,
      });
    } catch (e) {
      console.error(e);
    }
    setIsSendingLoading(false);
    closeModal();
  };

  return (
    <Dialog
      open={modalRef.sendTemplate}
      className="h-[80vh] flex flex-col"
      onClose={closeModal}
    >
      <DialogTitle close={true}>Send a template</DialogTitle>
      <DialogBody className="flex-1 overflow-y-auto scrollbar">
        <div>
          {allTemplatesOptions ? (
            <SelectInput
              options={allTemplatesOptions}
              selected={selectedTemplate}
              onChange={(value) => setSelectedTemplate(value)}
              onSelect={(value) => setSelectedTemplate(value)}
              value={selectedTemplate}
            />
          ) : (
            <LoadingSpinner />
          )}
          <br />
          {template && template.body && (
            <TemplatePreview
              selectedHeader={template.header}
              body={template.body}
              footer={template.footer}
              quickReply1={template.quickReply1}
              quickReply2={template.quickReply2}
              quickReply3={template.quickReply3}
              urlButton={template.urlButton}
              width="100%"
              imageMaxHeight="300px"
              isUrlButtonAtBottom={template.isUrlButtonAtBottom}
              cards={template.cards}
            />
          )}
        </div>
      </DialogBody>
      <DialogActions>
        <Button
          color="primaryButton"
          onClick={() => sendTemplate(selectedTemplate)}
          loading={isSendingLoading}
          disabled={!selectedTemplate}
        >
          Send this template
        </Button>
      </DialogActions>
    </Dialog>
  );
}
